// The support mailbox on the sign-in screen as an ICON in the top-right corner
// (owner, 5 Oct 2026: not inside the sign-in form). A tap opens a popup with
// the address and the no-medical-records warning. No address on this stack
// (GET /api/support-contact answers null) → no button at all.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Headset } from 'lucide-react';
import ModalShell from '../common/ModalShell';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import { useSupportEmail } from '../../utils/supportContact';
import SupportContactNote from './SupportContactNote';

export default function SupportContactButton({ className = '' }) {
  const { t } = useTranslation();
  const axiosInstance = useAxiosAuth();
  const email = useSupportEmail(() => axiosInstance?.get(`${baseURL}/support-contact`));
  const [open, setOpen] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    // dialogRef holds the frame; focus its close button.
    dialogRef.current?.querySelector('[data-testid="support-contact-close"]')?.focus();
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!email) return null;
  const label = t('supportContact.button_label', 'Help signing in');
  return (
    <>
      <button
        type="button"
        data-testid="support-contact-button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className={className}
      >
        <Headset className="h-6 w-6" />
      </button>
      {open && (
        // A press on the dimmed page closes, as before the shared frame.
        <div ref={dialogRef} onMouseDown={(e) => { if (e.target === e.currentTarget.firstElementChild) setOpen(false); }}>
          <ModalShell
            title={t('supportContact.popup_title', 'Need help signing in?')}
            onClose={() => setOpen(false)}
            closeLabel={t('common.close', 'Close')}
            size="md"
            zClass="z-[100]"
            testId="support-contact-popup"
            closeTestId="support-contact-close"
          >
            <div className="text-left">
              <SupportContactNote variant="signin" />
            </div>
          </ModalShell>
        </div>
      )}
    </>
  );
}
